<template>
  <validation-observer v-slot="{ handleSubmit, meta }">
    <ld-modal-content :title="selectedForm?.name ? selectedForm.name : 'Select Action Template'" :class="{'form-selected': selectedFormId, 'form-selection': !selectedFormId}" class="form-modal form-fill-container">
      <template #header-left-buttons v-if="selectedFormId">
        <button class="button is-white" @click="submission ? $emit('close') : (selectedFormId = null, selectedForm = {})">
          <span class="icon">
            <i class="mdi mdi-arrow-left"></i>
          </span>
          <span>{{ submission ? 'Back' : 'Action Templates' }}</span>
        </button>
      </template>
      <template #subtitle v-if="selectedForm.description">
        <span v-tooltip="{ theme: 'lookdeep-global', content: selectedForm.description, placement: 'bottom', disabled: !isTruncated(selectedForm.description) }">{{ truncateDescription(selectedForm.description) }}</span>
      </template>
      <template #header-right-buttons>
        <button class="button is-white" v-if="submission" @click="showHistory = !showHistory">
          <span class="icon">
            <i :class="['mdi', showHistory ? 'mdi-form-textbox' : 'mdi-history']"></i>
          </span>
          <span>{{ showHistory ? 'Action' : 'History' }}</span>
        </button>
      </template>
      <template #default>
        <div class="choose-form" v-if="!selectedFormId">
          <b-select v-model="selectedFormId" @input="formSelected" expanded>
            <option :value="null">Select an Action Template</option>
            <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name }}</option>
          </b-select>
        </div>
        <template v-if="selectedFormId">
          <div class="form-content">
            <template v-if="showHistory">
              <field-history-content object-type="FormSubmission" :object-id="submission.id.toString()"></field-history-content>
            </template>
            <template v-else-if="viewOnly">
              <b-field v-for="item in submission.results" :label="item.key" :key="item.key">
                <p class="submission__value">{{ Array.isArray(item.value) ? item.value.join(', ') : item.value }}</p>
              </b-field>
            </template>
            <template v-else>
              <template v-for="(field, index) in selectedForm.fields">
                <!-- eslint-disable-next-line vue/valid-v-for -->
                <component :is="`${field.type}-element-renderer`" v-model="filledValues[index].value" v-bind="field"></component>
              </template>

              <b-field label="" v-if="selectedForm.can_update">
                <b-checkbox v-model="isCompleted" :disabled="status === 'completed'">Completed</b-checkbox>
              </b-field>
            </template>

          </div>
        </template>
      </template>
      <template #footer v-if="!showHistory && !viewOnly">
        <div class="action-buttons">
          <button class="button is-white" @click="$emit('close')">Cancel</button>
          <button class="button is-primary" v-if="selectedFormId" :disabled="!meta.valid" @click="handleSubmit(save)">Save</button>
        </div>
      </template>
    </ld-modal-content>
  </validation-observer>
</template>

<script>
import to from "@/lib/to";
import RendererComponents from "@/components/form_builder/RendererComponents";
import LdModalContent from "@/components/LdModalContent";
import ValidationObserver from "@/components/validation/ValidationObserver";
import FieldHistoryContent from "@/components/FieldHistoryContent";


export default {
  name: "FormFill",
  components: {ValidationObserver, LdModalContent, FieldHistoryContent, ...RendererComponents},
  props: ['patientId', 'patientMonitorId', 'submission', 'viewOnly'],
  async mounted() {
    const [err, response] = await to(this.http.get('/forms/dropdown'))
    if (!err) {
      this.forms = response.data.filter( x => x.is_active)
    }

    if (this.submission) {
      this.isEditing = true
      this.selectedFormId = this.submission.form.id
      this.formSelected({ target: { value: this.submission.form.id } })

      this.$nextTick(() => {
        if (Array.isArray(this.submission.results)) {
          this.submission.results.forEach((result, index) => {
            if (this.filledValues[index]) {
              this.filledValues[index].value = result.value
            }
          })
          this.status = this.submission.status
        }
      })
    }
  },
  methods: {
    truncateDescription(description) {
      return this.isTruncated(description) ? description.substring(0, 50) + '...' : description
    },
    isTruncated(description) {
      return description && description.length > 50
    },
    formSelected({ target: { value: formId } }) {
      if (!formId) return;
      const form = this.forms.filter(x => x.id == formId)[0]
      this.filledValues = []
      form.fields.forEach(field => {
        this.filledValues.push({
          key: field.label,
          value: field.defaultValue
        })
      })
      this.selectedForm = form
    },
    async save() {
      const payload = {
        patient_monitor_id: parseInt(this.patientMonitorId) || null,
        patient_id: parseInt(this.patientId),
        form_id: this.selectedFormId,
        results: this.filledValues,
        status: this.isCompleted ? 'completed' : 'in_progress'
      }
      
      let err
      if (this.isEditing && this.submission) {
        [err] = await to(this.http.put(`form_submissions/${this.submission.id}`, payload))
      } else {
        [err] = await to(this.http.post('form_submissions', payload))
      }
      
      if (!err) {
        this.$emit('close')
        this.$emit('save')
        this.$buefy.toast.open({
          message: this.isEditing ? 'Action successfully updated' : 'Action successfully submitted',
          type: 'is-success'
        })
      }
    }
  },
  data() {
    return {
      forms: [],
      selectedFormId: null,
      selectedForm: {},
      filledValues: [],
      isEditing: false,
      status: 'in_progress',
      isCompleted: false,
      activeTab: 0,
      showHistory: false
    }
  }
}
</script>

<style scoped lang="scss">
  .form-fill-container {
    container-type: inline-size;
    container-name: form-fill;
  }

  .choose-form h1 {
    font-size: 23px;
    font-weight: 600;
    line-height: 26px;
    margin-bottom: 20px;
    text-align: center;
  }

  .form-modal {
    max-width: 600px;
    overflow: visible;
    :deep(.ld-modal__header) {
      border-radius: 8px 8px 0 0;
    }
  }

  @container form-fill (max-width: 500px) {
    .form-modal :deep(.ld-modal__header-left button span:not(.icon)) {
      display: none;
    }
  }

  .choose-form__title {
    font-size: 14px;
    color: #000000;
    white-space: nowrap;
  }
  .action-buttons {
    margin: 10px 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    > button:first-child {
      margin-right: 10px;
    }
  }
  .form-content {
    overflow-y: auto;
  }
  .description-button {
    width: 26px;
    height: 26px;
    padding: 0;
    i {
      font-size: 17px;
    }
  }
  .form-description {
    white-space: pre-line;
    padding: 10px;
    max-width: 380px;
    max-height: 200px;
    overflow-y: auto;
  }
  .form-tabs {
    margin: 0 -20px;
    :deep(.tabs ul) {
      padding: 0 11px;
    }
    :deep(.tab-content) {
      display: block !important;
      padding: 10px 20px;
    }
  }
  .submission__value {
    white-space: pre-line;
    line-height: 20px;
    font-size: 15px;
  }
</style>
